// Status tag with the failure detail on hover, plus the retry affordance. A
// running execution shows only a spinner-ish hourglass and no tag.

import React from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { Button, Flex, H4, P, Popover, Tag } from '@decisiv/ui-components';
import Hourglass from '@decisiv/iconix/lib/components/Hourglass';
import Redo from '@decisiv/iconix/lib/components/Redo';

import describeStatus from './executionStatus';
import { executionAttributesPropTypes } from './executionPropTypes';

const AUTOMATIC_RETRY = 'AUTOMATIC_RETRY';

const RetryButton = ({
  row,
  attributes,
  retry,
  executions,
  maxRetryCount = 0,
}) => {
  const retryCount = attributes.retry_count;

  return (
    <Popover
      placement="bottom"
      manageEvents="hover"
      target={({ ref }) => (
        <Button
          icon={Redo}
          ref={ref}
          aria-label={t`Retry`}
          // An automatic retry is the system's own attempt; retrying it by hand
          // would stack a second chain on top.
          disabled={
            attributes.source === AUTOMATIC_RETRY
              ? true
              : retry.isRetryButtonDisabled(
                  executions,
                  attributes.x_decisiv_trace_id,
                )
          }
          loading={retry.retryExecutionIdRef.current === row.id}
          variant="ghost"
          style={{ cursor: 'pointer', marginLeft: '5px' }}
          onClick={() =>
            retry.handleRetry(
              row.id,
              attributes.configured_integration_id,
              retry.onRetried,
            )
          }
        />
      )}
    >
      <Flex padding={1}>
        <span>
          {retryCount && maxRetryCount
            ? `${t`Auto Retry `} ${retryCount} ${t`of`} ${maxRetryCount}`
            : t`Retry`}
        </span>
      </Flex>
    </Popover>
  );
};

RetryButton.propTypes = {
  row: PropTypes.oneOfType([PropTypes.object]).isRequired,
  attributes: executionAttributesPropTypes.isRequired,
  retry: PropTypes.oneOfType([PropTypes.object]).isRequired,
  executions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object]))
    .isRequired,
  maxRetryCount: PropTypes.number,
};
export default function StatusCell({
  row,
  retry,
  executions,
  maxRetryCount = 0,
}) {
  const { attributes } = row;
  const { status } = attributes;
  const { color, text, icon, errorTitle, errorDesc } =
    describeStatus(attributes);

  if (status === 'RUNNING') {
    return (
      <Flex alignItems="center">
        <Popover
          placement="bottom-start"
          manageEvents="hover"
          target={({ ref }) => (
            <Button
              icon={Hourglass}
              ref={ref}
              aria-label={text}
              variant="ghost"
              style={{ cursor: 'pointer', marginLeft: '20px' }}
            />
          )}
        >
          <Flex padding={1}>
            <span>{text}</span>
          </Flex>
        </Popover>
      </Flex>
    );
  }

  // A row with no status at all renders nothing rather than a blank tag.
  if (!status) return null;

  return (
    <Flex alignItems="center">
      <Popover
        placement="bottom"
        manageEvents="hover"
        target={({ ref, toggle }) => (
          <Tag
            ref={ref}
            color={color}
            icon={icon}
            text=""
            aria-label={text}
            variant="outline"
            action={toggle}
            actionIcon={icon}
            actionAttributes={{ 'aria-label': text }}
            style={{ marginLeft: '15px' }}
          />
        )}
      >
        <Flex padding={1} flexDirection="column">
          {errorTitle && errorDesc ? (
            <>
              <H4 weight="default">{text}</H4>
              <P marginTop={0.5} shade={0}>
                <span style={{ fontWeight: '600' }}>title: </span>
                {errorTitle}
              </P>
              <P marginTop={0.5} shade={0} truncate>
                <span style={{ fontWeight: '600' }}>detail: </span>
                {errorDesc}
              </P>
            </>
          ) : (
            <span>{text}</span>
          )}
        </Flex>
      </Popover>
      {retry.isRetryButtonVisible(attributes) && (
        <RetryButton
          row={row}
          attributes={attributes}
          retry={retry}
          executions={executions}
          maxRetryCount={maxRetryCount}
        />
      )}
    </Flex>
  );
}

StatusCell.propTypes = {
  row: PropTypes.oneOfType([PropTypes.object]).isRequired,
  retry: PropTypes.oneOfType([PropTypes.object]).isRequired,
  executions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object]))
    .isRequired,
  maxRetryCount: PropTypes.number,
};
